/** Same bounds the server enforces and `GuestCountDialog` types against. */
export const GUEST_COUNT_MIN = 1;
export const GUEST_COUNT_MAX = 500;

/** Clamped stepper arithmetic — "min 1 via −" (covers rework, Phase 2b). */
export function nextGuestCountValue(current: number, delta: number): number {
  return Math.min(
    GUEST_COUNT_MAX,
    Math.max(GUEST_COUNT_MIN, current + delta),
  );
}

/**
 * Serializes rapid stepper taps into ONE `setGuestCount` write in flight at a
 * time (covers rework, Phase 2b).
 *
 * `setGuestCount` carries no `expectedVersion` — unlike `closeInvoice` /
 * `settleInvoice`, it is not client-version-guarded. So the risk a bare
 * `onClick={() => sendCommand(...)}` would create here is not a typed version
 * conflict; it is a LOST WRITE — two overlapping requests racing the server in
 * whatever order the network happens to deliver them, where the LAST tap can
 * be the one that loses to an EARLIER one that happened to arrive second.
 *
 * This queue removes the race by construction rather than by locking the UI:
 * a tap while a write is already in flight does not fire a second request, it
 * REPLACES whatever value was pending (coalescing — three taps in one gap
 * still produce one follow-up write, for the LAST value asked for, never
 * three). The write in flight always finishes and is never abandoned, and the
 * moment it returns, the queue sends whatever the latest tap asked for. Never
 * more than one request in flight, and never a tap dropped on the floor.
 *
 * `request` takes its `send` function on EVERY call rather than once at
 * construction, on purpose: the queue itself is built once and held in a
 * `useRef` for the life of the screen (so its in-flight/pending state
 * survives across renders), but the invoice command dispatcher (`run` in
 * `InvoicePage`) is a plain closure re-created every render. Baking a
 * specific render's `send` into the ref at construction time is exactly the
 * stale-closure trap `react-hooks/refs` exists to catch; taking `send` at
 * call time means every request always uses the CURRENT render's dispatcher,
 * the same way an ordinary `onClick` always does, with no ref indirection
 * needed to keep it fresh.
 */
export function createGuestCountWriteQueue(onIdle?: () => void) {
  let inFlight = false;
  // The value most recently ASKED for, until the queue drains. A tap computes
  // its "+1" from this rather than from the last rendered count: two taps that
  // land before React re-renders would otherwise both read the same count and
  // ask for the same number, silently losing one.
  let lastRequested: number | null = null;
  let pending: {
    value: number;
    send: (value: number) => Promise<unknown>;
  } | null = null;

  const pump = (value: number, send: (value: number) => Promise<unknown>) => {
    inFlight = true;
    void send(value).finally(() => {
      inFlight = false;
      if (pending !== null) {
        const next = pending;
        pending = null;
        pump(next.value, next.send);
      } else {
        lastRequested = null;
        onIdle?.();
      }
    });
  };

  return {
    /** Ask for `value` to be the bill's covers count, via `send`. */
    request(value: number, send: (value: number) => Promise<unknown>) {
      lastRequested = value;
      if (inFlight) {
        pending = { value, send };
        return;
      }
      pump(value, send);
    },
    /** The latest value asked for while writes are outstanding, else null. */
    latest: () => lastRequested,
    /** For tests: whether a write is currently in flight. */
    isInFlight: () => inFlight,
  };
}
